<template>
	<header>
		<nav class="main fix-on-scroll" :class="page && page.headingImage ? '' : 'no-slider'">
			<div class="row">
				<div class="columns column12 flex-row">
					<div v-if="showMenu" class="menu-button button" @click="hideMenu">
						<font-awesome-icon icon="fal fa-times" size="1x" />
						<span>{{ $t('close') }}</span>
					</div>
					<div v-else class="menu-button button" @click="toggleMenu">
						<div class="bars">
							<div class="bar" />
							<div class="bar" />
							<div class="bar" />
						</div>
						<span>{{ $t('menu') }}</span>
					</div>
					<nuxt-link class="logo" aria-label="Back to homepage" :to="localePath('/')" />
					<div class="nav-buttons">
						<div class="lang-select" @click="toggleLangNav">
							<font-awesome-icon icon="fa-light fa-globe" size="1x" />
							<span class="locale">{{ locale }}</span>
							<font-awesome-icon icon="fa-light fa-chevron-down" size="1x" />
							<div v-if="showLangNav" class="lang-wrapper">
								<div v-for="item in page.langmenu" :key="`${item.filename}-${item.language}`">
									<nuxt-link
										class="flag"
										:class="item.language"
										:to="`${item.filename}`"
										@click="hideMenu"
									>
										{{ item.language }}
									</nuxt-link>
								</div>
							</div>
						</div>
						<a :href="`mailto:${defaults[locale].website.email}`">
							<font-awesome-icon icon="fa-light fa-envelope" size="1x" />
						</a>
						<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
							<font-awesome-icon icon="fa-light fa-phone" size="1x" />
						</a>
						<book-button class="button button-cta reserve-now right" aria-label="Reserve now">
							<font-awesome-icon icon="fa-light fa-calendar-days" size="1x" />
							<span>{{ $t('bookNow') }}</span>
						</book-button>
					</div>
				</div>
			</div>
			<div class="nav-container" :class="{ active: showMenu }">
				<div class="row">
					<ul class="main-nav">
						<li
							v-for="item in topmenu[locale]"
							:key="item.filename"
							:class="{ 'has-dropdown': item.submenu.length }"
						>
							<nuxt-link :exact="item.slug === 'index'" :to="item.filename" @click="hideMenu">
								{{ item.header }}
							</nuxt-link>
							<submenu
								v-if="item.submenu.length"
								:hide-all="!showMenu"
								:item="item"
								:subitems="item.submenu"
								@hide-menu="hideMenu"
							/>
						</li>
					</ul>
					<div v-if="page" class="lang-nav">
						<div v-for="item in page.langmenu" :key="`${item.filename}-${item.language}`">
							<nuxt-link class="flag" :class="item.language" :to="`${item.filename}`" @click="hideMenu">
								<span>{{ $t(item.language) }}</span>
							</nuxt-link>
						</div>
					</div>
					<div class="nav-contact">
						<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
							<font-awesome-icon icon="fa-light fa-phone" size="1x" />
							<span>{{ defaults[locale].website.phone }}</span>
						</a>
						<a :href="`mailto:${defaults[locale].website.email}`">
							<font-awesome-icon icon="fa-light fa-envelope" size="1x" />
							<span>{{ defaults[locale].website.email }}</span>
						</a>
						<social-media :socials="socials" />
					</div>
				</div>
			</div>
		</nav>
	</header>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, socials, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();
await fetchDefaults();

const showLangNav = ref(false);
const showMenu = ref(false);

const { data: topmenu } = await useWebsiteFetch('topmenu');

const hideMenu = () => {
	showMenu.value = false;
	document.body.classList.remove('locked');
};

const toggleMenu = () => {
	showMenu.value = true;
	document.body.classList.add('locked');
};

const toggleLangNav = () => {
	showLangNav.value = !showLangNav.value;
};

const handleScroll = () => {
	const fixedElements = document.querySelectorAll('.fix-on-scroll');
	if (window.pageYOffset >= 50) {
		fixedElements.forEach((el) => el.classList.add('scroll'));
	} else {
		fixedElements.forEach((el) => el.classList.remove('scroll'));
	}
};

onBeforeMount(() => {
	window.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
	window.removeEventListener('scroll', handleScroll);
});
</script>

<style lang="scss" scoped>
.logo {
	background: url('~/assets/images/logo-hotelbreathe-leiden-white.png') no-repeat center center;
	background-size: 200px;
	width: 200px;
	height: 77px;
	position: absolute;
	left: 0;
	right: 0;
	margin: 0 auto;
	animation: fade-from-top 1s ease;
}

nav.main {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 100;
	padding: 30px 0;
	transition: all 0.3s ease-in-out;
	overflow: visible;

	&.scroll,
	&.no-slider {
		padding: 18px;
		background: rgb(87 119 154 / 80%);
		backdrop-filter: blur(12px);
	}
}

nav .row {
	overflow: visible;
	z-index: 102;
	position: relative;
}

.menu-button {
	background: none;
	padding: 0;
	margin: 0;
	font-size: 24px;
	font-weight: 400;
	cursor: pointer;
	text-align: left;
	animation: fade-from-left 0.7s ease;
	display: flex;
	flex-flow: row nowrap;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
	opacity: 0.85;

	i,
	svg {
		width: 36px;
		font-size: 26px;
		margin: 0;
	}

	&:hover {
		opacity: 1;
		background: none;

		.bars {
			.bar {
				width: 36px !important;
				margin-left: 0;
				margin-right: 0;
			}
		}
	}
}

.bars {
	display: flex;
	flex-flow: column wrap;
	justify-content: space-between;
	align-items: center;
	margin: 0 4px 0 0;

	.bar {
		width: 36px;
		height: 2px;
		background: #fff;
		margin: 4px 0;
		transition: all 0.3s ease-in-out;

		&:last-child {
			width: 24px;
			margin: 4px 0 0 12px;
		}

		&:first-child {
			width: 24px;
			margin: 0 12px 4px 0;
		}
	}
}

nav .flex-row,
.nav-buttons {
	justify-content: space-between;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
}

.nav-buttons {
	gap: 22px;
	color: #fff;
	animation: fade-from-right 0.7s ease;

	a {
		color: #fff;
		text-decoration: none;
		margin: 0;
		opacity: 0.85;

		&.button,
		&:hover {
			opacity: 1;
		}
	}
}

.reserve-now {
	display: flex;
	flex-flow: row nowrap;
	justify-content: center;
	align-items: center;
	padding: 10px 25px 10px 20px;

	svg {
		margin: 0 12px 2px 0;
	}
}

.nav-container {
	background: rgb(127 124 119 / 90%);
	backdrop-filter: blur(17px);
	padding: 30px 50px;
	position: fixed;
	top: 0;
	left: 0;
	padding-top: 80px;
	z-index: 101;
	width: 100%;
	height: 100%;
	min-height: 100vh;
	opacity: 0;
	transition: all 0.4s ease-in-out;
	transform: translate(0, 100%) matrix(1, 0, 0, 1, 0, 0);

	.row {
		display: flex;
		flex-flow: column nowrap;
		justify-content: space-between;
		align-items: center;
		height: 100%;
	}
}

.nav-container.active {
	transform: matrix(1, 0, 0, 1, 0, 0);
	opacity: 1;
}

.lang-nav {
	display: block;
	list-style: none;
	text-align: left;
	position: absolute;
	right: 0;
	bottom: 0;
	line-height: 20px;

	a {
		color: #fff;
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		justify-content: flex-start;
		font-size: 16px !important;
		line-height: 32px !important;
		gap: 5px;
		font-weight: 500;
		font-family: $heading-font-family;

		&.router-link-exact-active,
		&:hover {
			color: $cta-color;
		}
	}

	img {
		max-width: 16px;
		float: left;
		margin: 0 5px 0 0;
	}
}

.lang-select {
	padding: 12px 15px;
	border-right: 1px solid $alternating-background-color;
	cursor: pointer;
	position: relative;
	text-transform: uppercase;

	.locale {
		padding: 0 3px;
	}

	svg {
		font-size: 16px;
		margin: 0 4px 0 0;

		&:last-child {
			font-size: 11px;
			margin: 0 0 0 2px;
		}
	}
}

.lang-wrapper.active,
.lang-wrapper a {
	display: flex;
	flex-flow: column nowrap;
	place-content: space-between flex-start;
	color: #000;
	gap: 5px;
}

.lang-wrapper {
	position: absolute;
	top: 35px;
	left: 3px;
	right: 3px;
	padding: 10px 9px;
	text-align: center;
	background: #fff;
	box-shadow: 0 18px 18px rgb(0 0 0 / 10%);
	border-radius: 3px;
	z-index: 90;
	margin: 0 auto;
	max-width: 70px;

	a {
		width: 100%;
		flex-flow: row nowrap;
		align-items: center;
		margin: 6px 0;
		font-size: 12px;
	}

	img {
		width: 16px;
		height: 16px;
		margin: 2px 0 0;
	}
}

.mobile-lang-nav {
	margin: 40px 0 0;
	font-size: 14px;
	display: none;

	img {
		max-width: 16px;
		margin: 0 10px 0 0;
	}
}

.main-nav .mobile-lang-nav a {
	width: 100%;
	display: block;
	font-size: 16px;
	line-height: 28px;
}

/* Fullscreen Nav */
.main-nav {
	list-style: none;
	min-width: 25%;
	text-align: center;
	padding-top: 150px;

	a {
		color: #fff;
		font-size: 38px;
		line-height: 62px;
		font-weight: 500;
		font-family: $heading-font-family;

		&:hover,
		&.active,
		&.router-link-exact-active {
			color: $cta-color;
		}
	}
}

.nav-contact {
	position: absolute;
	left: 0;
	bottom: 0;
	text-align: left;

	svg {
		margin-right: 5px;
	}
}

.nav-contact > a {
	width: 100%;
	color: #fff;
	display: block;
	font-weight: 300;
	margin: 0 0 15px;
}

.nav-contact > a i {
	margin: 0 10px 0 0;
}

.nav-contact .socials {
	margin: 20px 0 0;
}

.close-wrapper {
	align-self: flex-start;
}

.nav-close {
	color: #fff;
	cursor: pointer;
	font-size: 60px;
	padding: 10px;
}

/* Breadcrumbs */
.breadcrumbs ul {
	list-style: none;
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-start;
	align-items: center;
	margin: 0 0 75px;
}

.breadcrumbs li {
	margin: 0 10px 0 0;
}

.breadcrumbs i {
	font-size: 11px;
}

.breadcrumbs a {
	color: $body-color;
}

.breadcrumbs a.active {
	color: $cta-color;
}

@media (max-height: 740px) {
	.nav-container {
		overflow-y: scroll;
	}
}

@media (max-height: 640px) {
	.lang-nav {
		position: relative;
		bottom: auto;
		left: auto;
		right: auto;
		margin-top: 20px;
		z-index: 1;
	}

	.main-nav {
		z-index: 2;
		position: relative;
	}
}

@media (max-width: 920px) {
	.reserve-now {
		padding: 0;

		svg {
			margin: 0;
		}
	}

	.menu-button {
		justify-content: center;
	}

	.lang-nav {
		display: block;
	}
}

@media (max-width: 800px) {
	.lang-nav,
	.nav-contact {
		position: relative;
		bottom: auto;
		left: auto;
		right: auto;
		float: left;
		margin-top: 30px;
	}

	.main-nav {
		a {
			font-size: 22px;
			line-height: 44px;
		}
	}
}

@media (max-width: 680px) {
	.main-nav {
		min-height: 60vh;
	}
}

@media (max-width: 600px) {
	.nav-container {
		padding: 10px 25px 25px;
	}

	.main-nav,
	.contact-info,
	.lang-nav {
		width: 100%;
	}

	nav.main {
		.flex-row {
			padding: 0;
		}
	}

	nav.main.scroll,
	nav.main.no-slider {
		padding: 18px 6px;
	}

	.contact-info {
		margin: 25px auto 0;
	}

	nav .buttons .button {
		svg {
			margin-right: 0;
			font-size: 16px;
		}

		span {
			display: none;
		}
	}
}
</style>
